import React, { ReactNode, createContext, useCallback, useContext, useState } from 'react';

import { LS_FORECASTING_ONBOARDING_STEP } from '../../../../predict/src/forecast/onboarding/const';

export enum HELP_MENU_EVENTS {
  close = 'help-menu-close',
  open = 'help-menu-open'
}

interface HelpMenuContextData {
  anchorElement: HTMLElement | null;
  closeMenu: () => void;
  isOpen: boolean;
  openMenu: (anchorEl?: HTMLElement) => void;
  setDefaultAnchorElement: (anchorEl: HTMLElement) => void;
}

interface HelpMenuContextProps {
  children?: ReactNode;
}

const HelpMenuContext = createContext<HelpMenuContextData>(({} as unknown) as HelpMenuContextData);

const useContextSetup = (): HelpMenuContextData => {
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
  const [defaultAnchorElement, setDefaultAnchorElement] = useState<HTMLElement>();
  const isOpen = !!anchorElement;

  const closeMenu = useCallback(() => {
    localStorage.removeItem(LS_FORECASTING_ONBOARDING_STEP);
    setAnchorElement(null);
    window.dispatchEvent(new CustomEvent(HELP_MENU_EVENTS.close));
  }, []);

  const openMenu = useCallback(
    (anchorEl?: HTMLElement) => {
      localStorage.removeItem(LS_FORECASTING_ONBOARDING_STEP);
      const newAnchor = anchorEl || defaultAnchorElement;
      if (newAnchor) {
        setAnchorElement(newAnchor);
        window.dispatchEvent(new CustomEvent(HELP_MENU_EVENTS.open));
      }
    },
    [defaultAnchorElement]
  );

  return {
    anchorElement,
    closeMenu,
    isOpen,
    openMenu,
    setDefaultAnchorElement
  };
};

export const HelpMenuContextProvider = ({ children }: HelpMenuContextProps) => {
  const value = useContextSetup();
  return <HelpMenuContext.Provider value={value}>{children}</HelpMenuContext.Provider>;
};

export const useHelpMenuContext = () => useContext(HelpMenuContext);
